import {
  IDEMPOTENCY_KEY_HEADER,
  type ChangeTicketState,
  type DiscardTicketInput,
  type ResolvedTicket,
  type ResolveTicketInput,
  type SetTicketCorrectionInput,
  type TicketDetail,
} from '@easyhelp/shared';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest } from './client';
import { ticketQueryKey, ticketsQueryKey } from './tickets';

/**
 * Correzione dello sviluppo, chiusura, scarto e riapertura (story #6). Le azioni che rispondono con
 * il dettaglio lo mettono in cache e invalidano il resto della radice `['tickets']` (coda,
 * contatori, «Risolti»), come il cambio di priorità; «Invia e chiudi» invalida tutto, come il composer.
 */

const ticketPath = (code: string, action: string) => `/tickets/${encodeURIComponent(code)}/${action}`;

async function storeDetail(queryClient: QueryClient, code: string, detail: TicketDetail): Promise<void> {
  const detailKey = ticketQueryKey(code);
  queryClient.setQueryData(detailKey, detail);
  await queryClient.invalidateQueries({
    queryKey: ticketsQueryKey,
    predicate: (query) => !detailKey.every((part, index) => query.queryKey[index] === part),
  });
}

/** Una mutazione che risponde con il dettaglio aggiornato. */
function useDetailMutation<TInput>(code: string, request: (input: TInput) => Promise<TicketDetail>) {
  const queryClient = useQueryClient();
  return useMutation<TicketDetail, ApiError, TInput>({
    mutationFn: request,
    onSuccess: (detail) => storeDetail(queryClient, code, detail),
  });
}

/** `PUT /tickets/:code/correction`: registra la correzione dello sviluppo senza chiudere. */
export function useSetTicketCorrection(code: string) {
  return useDetailMutation<SetTicketCorrectionInput>(code, (input) =>
    apiRequest<TicketDetail>(ticketPath(code, 'correction'), { method: 'PUT', body: JSON.stringify(input) })
  );
}

/** `POST /tickets/:code/discard`: «Scarta» con motivo ed eventuale ticket originale. */
export function useDiscardTicket(code: string) {
  return useDetailMutation<DiscardTicketInput>(code, (input) =>
    apiRequest<TicketDetail>(ticketPath(code, 'discard'), { method: 'POST', body: JSON.stringify(input) })
  );
}

/** `POST /tickets/:code/close` («Chiudi») o `/reopen` («Riapri»): solo il responsabile. */
export function useCloseOrReopenTicket(code: string) {
  return useDetailMutation<'close' | 'reopen'>(code, (action) =>
    apiRequest<TicketDetail>(ticketPath(code, action), { method: 'POST' })
  );
}

/** `PUT /tickets/:code/state`: cambio di stato manuale fra In lavorazione e In attesa dell'utente. */
export function useChangeTicketState(code: string) {
  return useDetailMutation<ChangeTicketState['state']>(code, (state) =>
    apiRequest<TicketDetail>(ticketPath(code, 'state'), { method: 'PUT', body: JSON.stringify({ state }) })
  );
}

/** «Invia e chiudi» con la chiave di idempotenza della sua bozza. */
export interface ResolveDraft {
  input: ResolveTicketInput;
  /** Una per bozza, riusata sui ritentativi: la stessa chiave non rispedisce l'email. */
  idempotencyKey: string;
}

/** `POST /tickets/:code/resolve`: email di chiusura a chi ha segnalato e passaggio a `Risolto`. */
export function useResolveTicket(code: string) {
  const queryClient = useQueryClient();
  return useMutation<ResolvedTicket, ApiError, ResolveDraft>({
    mutationFn: ({ input, idempotencyKey }) =>
      apiRequest<ResolvedTicket>(ticketPath(code, 'resolve'), {
        method: 'POST',
        headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ticketsQueryKey }),
  });
}
